import React, { useEffect, useState } from "react";
import { getCommittee, getCsrProjects } from "../csrApi";
import heroBg from "../assets/csr.jpg"; // swap for a dedicated CSR hero image if you have one

// Backend serves uploaded photos from this origin (port 8000 by default).
// The API only stores relative paths like "/csr-media/xxx.jpg", so we
// prefix them here to get a full, loadable URL in the browser.
const MEDIA_BASE = process.env.REACT_APP_API_BASE_URL;
const mediaUrl = (path) => (path?.startsWith("http") ? path : `${MEDIA_BASE}${path}`);

const CATEGORY_FILTERS = [
  { label: "All", value: "all" },
  { label: "Education", value: "Education" },
  { label: "Health", value: "Health" },
  { label: "Environment", value: "Environment" },
  { label: "Livelihood", value: "Livelihood" },
];

const bgColors = [
  "bg-gradient-to-r from-blue-500 to-indigo-500",
  "bg-gradient-to-r from-green-400 to-green-600",
  "bg-gradient-to-r from-yellow-400 to-yellow-600",
  "bg-gradient-to-r from-pink-400 to-pink-600",
  "bg-gradient-to-r from-purple-400 to-purple-600",
];

/* ── Image lightbox with a Share button (native share sheet, falls back
   to copying the link to the clipboard) ── */
// const ImageLightbox = ({ src, onClose }) => {
//   if (!src) return null;

//   const [shareStatus, setShareStatus] = useState("");
const ImageLightbox = ({ src, onClose }) => {
  const [shareStatus, setShareStatus] = useState("");

  if (!src) return null;

  const handleShare = async (e) => {
    e.stopPropagation();
    try {
      if (navigator.share) {
        await navigator.share({ url: src });
      } else {
        await navigator.clipboard.writeText(src);
        setShareStatus("Link copied!");
        setTimeout(() => setShareStatus(""), 2000);
      }
    } catch {
      // user cancelled the share sheet — nothing to do
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <div className="relative" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute -top-4 -right-4 text-white text-3xl font-bold"
        >
          ×
        </button>
        <img
          src={src}
          alt="Preview"
          className="max-w-[90vw] max-h-[90vh] object-contain"
        />
        <button
          onClick={handleShare}
          className="absolute bottom-3 right-3 flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-white/90 hover:bg-white text-gray-800 shadow-lg"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
          </svg>
          {shareStatus || "Share"}
        </button>
      </div>
    </div>
  );
};

const CSR = () => {
  const [tab, setTab] = useState("projects"); // "projects" | "committee"
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    Promise.all([getCommittee(), getCsrProjects()])
      .then(([membersRes, projectsRes]) => {
        setMembers(membersRes.data);
        setProjects(projectsRes.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredProjects = projects.filter((p) =>
    selectedCategory === "all" ? true : p.category === selectedCategory
  );

  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div
        className="relative bg-cover bg-center h-[60vh] flex items-center justify-center"
        style={{ backgroundImage: `url(${heroBg})` }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white">
            Corporate Social Responsibility
          </h1>
          <p className="text-lg sm:text-xl md:text-2xl text-white mt-2 italic">
            Building Communities, Beyond Buildings
          </p>
        </div>
      </div>

      {/* Main Tabs: CSR Project / Committee */}
      <div className="flex justify-center gap-4 px-4 mt-8 mb-6">
        <button
          onClick={() => setTab("projects")}
          className={`text-white font-semibold px-8 py-3 rounded-2xl shadow-lg transition-all duration-300 hover:scale-105 bg-gradient-to-r from-blue-500 to-indigo-500 ${
            tab === "projects" ? "ring-4 ring-white ring-offset-2" : "opacity-70"
          }`}
        >
          CSR Project
        </button>
        <button
          onClick={() => setTab("committee")}
          className={`text-white font-semibold px-8 py-3 rounded-2xl shadow-lg transition-all duration-300 hover:scale-105 bg-gradient-to-r from-purple-400 to-purple-600 ${
            tab === "committee" ? "ring-4 ring-white ring-offset-2" : "opacity-70"
          }`}
        >
          Committee
        </button>
      </div>

      {loading && (
        <p className="text-center text-xl text-gray-500 py-10">Loading...</p>
      )}

      {/* ---------------- CSR Project tab ---------------- */}
      {!loading && tab === "projects" && (
        <>
          {/* Category filter buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 px-4 mb-10">
            {CATEGORY_FILTERS.map((cat, index) => {
              const isSelected = selectedCategory === cat.value;
              return (
                <button
                  key={cat.value}
                  onClick={() => setSelectedCategory(cat.value)}
                  className={`text-white font-semibold px-5 py-3 rounded-2xl shadow-lg transition-all duration-300 hover:scale-105
                  ${bgColors[index % bgColors.length]}
                  ${isSelected ? "ring-4 ring-white ring-offset-2" : ""}`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Projects grid */}
          <div className="px-6 mb-16 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-6">
            {filteredProjects.length === 0 ? (
              <p className="col-span-full text-center text-xl text-gray-600">
                No CSR projects in this category yet.
              </p>
            ) : (
              filteredProjects.map((p) => (
                <div
                  key={p.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition flex flex-col h-full"
                >
                  <div className="relative w-full aspect-[16/9] bg-gray-200 overflow-hidden flex-shrink-0">
                    <img
                      src={mediaUrl(p.photo)}
                      alt={p.title}
                      className="absolute inset-0 w-full h-full object-cover cursor-pointer"
                      onClick={() => setLightboxImage(mediaUrl(p.photo))}
                    />
                  </div>

                  <div className="p-4 flex flex-col flex-grow">
                    <h3 className="text-lg font-bold text-blue-800 leading-tight">
                      {p.title}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1 font-semibold">
                      Category: {p.category}
                    </p>

                    {p.description && (
                      <div className="text-sm text-gray-700 mt-auto pt-4">
                        <p>
                          {expandedDescriptions[p.id]
                            ? p.description
                            : p.description.slice(0, 100) +
                              (p.description.length > 100 ? "..." : "")}
                        </p>
                        {p.description.length > 100 && (
                          <button
                            onClick={() => toggleDescription(p.id)}
                            className="text-blue-600 underline text-sm mt-1"
                          >
                            {expandedDescriptions[p.id] ? "Read Less" : "Read More"}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

      {/* ---------------- Committee tab ---------------- */}
      {!loading && tab === "committee" && (
        <div className="px-6 mb-16">
          {members.length === 0 ? (
            <p className="text-center text-xl text-gray-600">
              No committee members added yet.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {members.map((m) => (
                <div
                  key={m.id}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition p-6 text-center"
                >
                  <img
                    src={mediaUrl(m.photo)}
                    alt={m.name}
                    className="w-28 h-28 object-cover rounded-full mx-auto mb-3 border-4 border-gray-100"
                  />
                  <p className="font-bold text-blue-800">{m.name}</p>
                  {m.position && (
                    <p className="text-sm text-gray-500">{m.position}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Image Lightbox (now with Share) */}
      <ImageLightbox src={lightboxImage} onClose={() => setLightboxImage(null)} />
    </div>
  );
};

export default CSR;